import { useEffect, useState, useMemo } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateFormat';
import { DateInput } from '../components/DateInput';
import { printHTML, downloadWord } from '../utils/printHelper';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash, Printer, FileDoc, MagnifyingGlass, Scroll, EnvelopeSimple } from '@phosphor-icons/react';
import { confirmDialog } from '@/components/ConfirmDialog';
import FizickoLiceAutocomplete from '../components/FizickoLiceAutocomplete';
import ClientAutocomplete from '../components/ClientAutocomplete';
import { COREIT_LOGO_DATA_URL } from '../assets/coreitLogo';

const EMPTY = {
  client_id: '',
  vrsta: 'OL',
  vl_naziv: '', vl_pib: '', vl_sjediste: '',
  vl_zastupnik_ime_prezime: '', vl_zastupnik_jmb: '',
  pn_ime_prezime: '', pn_ime_prezime_gen: '', pn_jmbg: '', pn_broj_lk: '',
  datum_izdavanja: new Date().toISOString().slice(0, 10),
  napomena: '',
};

function formatDMY(d) {
  if (!d) return '';
  const [y, m, dd] = d.split('-');
  return `${dd}.${m}.${y}`;
}

// Montenegrin genitive converter for personal names. Applied on autocomplete select.
// Always masculine assumption (most common in company grantor/grantee context).
// User can always manually edit the generated genitive in the text field.
function wordToGenitivM(w) {
  if (!w) return w;
  const upper = w.toUpperCase();
  // Surname -IĆ / -IC: add -A (JOKOVIĆ → JOKOVIĆA)
  if (upper.endsWith('IĆ')) return upper + 'A';
  if (upper.endsWith('IC')) return upper + 'A';
  // -O: drop O, add A (RADULO → RADULA, MARKO → MARKA)
  if (upper.endsWith('O')) return upper.slice(0, -1) + 'A';
  // -E: drop E, add A (RADULE → RADULA, DUŠKE → DUŠKA)
  if (upper.endsWith('E')) return upper.slice(0, -1) + 'A';
  // -A: drop A, add E (NIKOLA → NIKOLE)
  if (upper.endsWith('A')) return upper.slice(0, -1) + 'E';
  // Default (ends in consonant): add -A (PETAR → PETARA - imperfect but close)
  return upper + 'A';
}
function toGenitivImePrezime(ime, prezime) {
  const i = (ime || '').trim().split(/\s+/).filter(Boolean).map(wordToGenitivM).join(' ');
  const p = (prezime || '').trim().split(/\s+/).filter(Boolean).map(wordToGenitivM).join(' ');
  return [i, p].filter(Boolean).join(' ');
}

export default function PunomocjeCoreitPage() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser.role === 'admin' || currentUser.role === 'super_admin';

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get('/punomocja-coreit'),
        api.get('/clients'),
      ]);
      setItems(p.data || []);
      setClients(c.data || []);
    } catch (e) {
      toast.error('Greska pri ucitavanju');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    if (!q) return items;
    return items.filter(i =>
      (i.client_naziv || '').toLowerCase().includes(q) ||
      (i.pn_ime_prezime || '').toLowerCase().includes(q) ||
      (i.vl_pib || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const handleClientChange = (client_id) => {
    const c = clients.find(x => x.id === client_id);
    if (!c) { setForm({ ...form, client_id }); return; }
    // Build full legal name
    const fullNaziv = c.naziv_firme || c.skraceni_naziv || '';
    const sjediste = [c.adresa, c.grad].filter(Boolean).join(', ');
    setForm({
      ...form,
      client_id,
      vl_naziv: fullNaziv.toUpperCase(),
      vl_pib: c.pib || '',
      vl_sjediste: sjediste.toUpperCase(),
      vl_zastupnik_ime_prezime: (c.direktor || '').toUpperCase(),
      vl_zastupnik_jmb: c.jmb_direktora || '',
    });
  };

  const refreshFromClient = () => {
    const c = clients.find(x => x.id === form.client_id);
    if (!c) return toast.error('Prvo izaberite klijenta');
    const fullNaziv = c.naziv_firme || c.skraceni_naziv || '';
    const sjediste = [c.adresa, c.grad].filter(Boolean).join(', ');
    setForm({
      ...form,
      vl_naziv: fullNaziv.toUpperCase(),
      vl_pib: c.pib || '',
      vl_sjediste: sjediste.toUpperCase(),
      vl_zastupnik_ime_prezime: (c.direktor || '').toUpperCase(),
      vl_zastupnik_jmb: c.jmb_direktora || '',
    });
    toast.success('Podaci povuceni iz kartice klijenta');
  };

  const handleSave = async () => {
    if (!form.client_id) return toast.error('Izaberite klijenta');
    if (!form.pn_ime_prezime) return toast.error('Unesite ime i prezime punomocnika');
    if (!form.pn_jmbg) return toast.error('Unesite JMBG punomocnika');
    try {
      if (editingId) {
        await api.put(`/punomocja-coreit/${editingId}`, form);
        toast.success('Punomocje azurirano');
      } else {
        await api.post('/punomocja-coreit', form);
        toast.success('Punomocje kreirano');
      }
      setOpen(false);
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : (detail || 'Greska');
      toast.error(msg);
    }
  };

  const handleEdit = (p) => {
    setEditingId(p.id);
    setForm({
      client_id: p.client_id,
      vrsta: p.vrsta || 'OL',
      vl_naziv: p.vl_naziv || '', vl_pib: p.vl_pib || '', vl_sjediste: p.vl_sjediste || '',
      vl_zastupnik_ime_prezime: p.vl_zastupnik_ime_prezime || '',
      vl_zastupnik_jmb: p.vl_zastupnik_jmb || '',
      pn_ime_prezime: p.pn_ime_prezime || '',
      pn_ime_prezime_gen: p.pn_ime_prezime_gen || '',
      pn_jmbg: p.pn_jmbg || '',
      pn_broj_lk: p.pn_broj_lk || '',
      datum_izdavanja: p.datum_izdavanja || '',
      napomena: p.napomena || '',
    });
    setOpen(true);
  };

  const handleDelete = async (p) => {
    if (!await confirmDialog({ title: 'Brisanje punomocja', message: `Obrisati punomocje za ${p.client_naziv}?`, confirmText: 'Obrisi', variant: 'danger' })) return;
    try {
      await api.delete(`/punomocja-coreit/${p.id}`);
      toast.success('Obrisano');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Greska pri brisanju');
    }
  };

  const buildHtml = (p) => {
    const cert = (p.vrsta === 'EFI') ? 'CoreSign EFI' : 'CoreSign OL';
    const pnDisplay = (p.pn_ime_prezime_gen && p.pn_ime_prezime_gen.trim()) ? p.pn_ime_prezime_gen : (p.pn_ime_prezime || '_____________________');
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Specijalno punomocje</title>
<style>
@page { size: A4; margin: 22mm 24mm; }
* { margin:0; padding:0; box-sizing:border-box; }
html, body { height: 100%; }
body { font-family: 'Helvetica Neue Light', 'Helvetica Neue', 'HelveticaNeue-Light', Helvetica, Arial, sans-serif; font-weight: 300; font-style: italic; font-size: 10pt; color: #000; line-height: 1.6; }
.logo-wrap { text-align: right; margin-bottom: 10px; }
.logo-img { height: 64px; width: auto; display: inline-block; }
.title { text-align: center; font-weight: 700; font-style: normal; font-size: 22pt; text-transform: uppercase; margin: 56px 0 40px 0; letter-spacing: 3px; }
.body-text { text-align: justify; margin-bottom: 12px; font-size: 10pt; font-style: italic; }
ul { margin: 8px 0 12px 26px; }
ul li { margin-bottom: 6px; text-align: justify; font-size: 10pt; font-style: italic; }
.clause { text-align: justify; margin: 12px 0; font-size: 10pt; font-style: italic; }
.sig-tbl { width: 100%; border-collapse: collapse; margin-top: 80px; }
.sig-tbl td { vertical-align: bottom; padding: 0; font-size: 11.5pt; }
.sig-tbl .mp-cell { text-align: center; font-weight: 600; font-size: 12pt; letter-spacing: 2px; padding-bottom: 46px; }
.sig-tbl .line-row td { border-top: 1px solid transparent; padding: 0; }
.sig-tbl .line-row .line-cell { border-top: 1px solid #000; padding-top: 4px; text-align: center; }
.sig-tbl .date-cell { width: 50%; font-size: 11.5pt; padding-bottom: 46px; }
.sig-tbl .date-cell .dline { border-bottom: 1px solid #000; display: inline-block; min-width: 140px; padding: 0 4px; text-align: center; }
.sig-tbl .label-cell { text-align: center; font-size: 11.5pt; padding-top: 4px; }
.sig-tbl .label-cell .director { font-weight: 600; font-size: 12pt; margin-top: 2px; display: block; }
@media print { body { margin: 0; } }
</style></head><body>
<div class="logo-wrap"><img class="logo-img" src="${COREIT_LOGO_DATA_URL}" alt="coreit" /></div>

<div class="title">SPECIJALNO PUNOMOĆJE</div>

<p class="body-text">
  ${p.vl_naziv || '_______________________________________'}, PIB: ${p.vl_pib || '__________'},
  sa sjedištem na adresi ${p.vl_sjediste || '_______________________________'},
  koje zastupa ${p.vl_zastupnik_ime_prezime || '_____________________'}, JMB ${p.vl_zastupnik_jmb || '_____________'}
  („Vlastodavac") ovim opunomoćava
  ${pnDisplay}, JMBG ${p.pn_jmbg || '_____________'},
  broj lične karte ${p.pn_broj_lk || '__________'} („Punomoćnik"), da u ime i za račun Vlastodavaca:
</p>

<ul>
  <li>potpiše i preda sertifikacionom tijelu Coreit CA Zahtjev za izdavanje digitalnog sertifikata ${cert};</li>
  <li>zaključi Ugovor o izdavanju i upotrebi ${cert} sertifikata sa Sertifikacionim tijelom Coreit CA koje djeluje unutar Coreit d.o.o. Podgorica, PIB: 02775018.</li>
</ul>

<p class="clause">
  Potpisnik ovog punomoćja, u svojstvu fizičkog lica, je dalje saglasan da Punomoćnik može u ime i za račun potpisnika ovog Punomoćja, potpisati gorenavedenu dokumentaciju i preuzeti ${cert} sertifikat od Sertifikacionog tijela Coreit CA.
</p>

<p class="clause">Ovo Specijalno punomoćje nije prenosivo na treća lica.</p>

<p class="clause">
  Ovo Specijalno punomoćje stupa na snagu danom izdavanja i važiće sve do preduzimanja svih radnji u njemu naznačenih, ali ne duže od 30 dana od dana njegovog izdavanja.
</p>

<table style="width: 100%; border-collapse: collapse; margin-top: 60px;">
  <tr>
    <td colspan="3" style="text-align: right; font-size: 12pt; padding: 0 0 30px 0;">Za Vlastodavca</td>
  </tr>
  <tr>
    <td style="width: 35%; padding: 0;">&nbsp;</td>
    <td style="width: 15%; text-align: center; vertical-align: middle; font-weight: 600; font-size: 12pt; letter-spacing: 2px; padding: 0;">M.P.</td>
    <td style="width: 50%; text-align: right; vertical-align: middle; padding: 0;">
      <div style="display: inline-block; min-width: 260px; border-top: 1px solid #000; padding-top: 4px; text-align: center; font-size: 11.5pt;">
        <span style="font-weight: 600;">${p.vl_zastupnik_ime_prezime || ''}</span>
      </div>
    </td>
  </tr>
</table>

<div style="margin-top: 50px; font-size: 11.5pt;">
  <span style="margin-right: 8px;">Datum izdavanja</span>
  <span style="border-bottom: 1px solid #000; display: inline-block; min-width: 150px; padding: 0 4px; text-align: center;">${formatDMY(p.datum_izdavanja) || ''}</span>
</div>

<script>window.onload=function(){window.print();window.onafterprint=function(){window.close()}}</script>
</body></html>`;
  };

  const print = (p) => printHTML(buildHtml(p));
  const word = (p) => {
    const suffix = (p.vrsta === 'EFI') ? 'ElektronskiPotpis' : 'Token';
    downloadWord(buildHtml(p), `Punomocje_${suffix}_${(p.client_naziv||'klijent').replace(/\s+/g,'_')}.doc`);
  };
  const sendEmail = async (p) => {
    try {
      const clientObj = clients.find(c => c.id === p.client_id);
      const clientEmail = clientObj?.email || '';
      const to = window.prompt('Email adresa primaoca:', clientEmail);
      if (!to || !to.trim()) return;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to.trim())) { toast.error('Neispravna email adresa'); return; }
      toast.loading('Saljem email...', { id: 'send-punomocje' });
      const res = await api.post(`/punomocja-coreit/${p.id}/send-email`, {
        document_html: buildHtml(p),
        to_email: to.trim(),
      });
      toast.success(res.data?.message || 'Email poslat', { id: 'send-punomocje' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Greska pri slanju emaila', { id: 'send-punomocje' });
    }
  };

  return (
    <div className="space-y-6" data-testid="punomocje-coreit-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-medium text-slate-900 flex items-center gap-2">
            <Scroll size={22} weight="duotone" className="text-rose-600" />
            Punomoćje COREIT
          </h3>
          <p className="text-sm text-slate-500 mt-1">Specijalno punomoćje za izdavanje CoreSign OL (token) ili CoreSign EFI (elektronski potpis) digitalnog sertifikata</p>
        </div>
        <Button onClick={() => { setForm({ ...EMPTY, datum_izdavanja: new Date().toISOString().slice(0, 10) }); setEditingId(null); setOpen(true); }} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="punomocje-new-btn">
          <Plus size={16} className="mr-2" /> Novo punomoćje
        </Button>
      </div>

      <Card className="p-4 bg-white border-slate-200">
        <div className="relative max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pretraga po klijentu, punomocniku, PIB-u..." className="pl-9 bg-white border-slate-300" data-testid="punomocje-search" />
        </div>
      </Card>

      <Card className="overflow-hidden bg-white border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-semibold tracking-wider uppercase text-slate-600">
                <th className="px-4 py-3">Vlastodavac (klijent)</th>
                <th className="px-4 py-3">Vrsta</th>
                <th className="px-4 py-3">PIB</th>
                <th className="px-4 py-3">Zastupnik</th>
                <th className="px-4 py-3">Punomoćnik</th>
                <th className="px-4 py-3">JMBG</th>
                <th className="px-4 py-3">Datum</th>
                <th className="px-4 py-3 text-right">Akcije</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-400">Ucitavanje...</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-400">Nema zapisa</td></tr>}
              {filtered.map(p => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`punomocje-row-${p.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{p.client_naziv || '-'}</td>
                  <td className="px-4 py-3">
                    {(p.vrsta === 'EFI')
                      ? <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold bg-indigo-100 text-indigo-700 uppercase tracking-wider">E-potpis</span>
                      : <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 uppercase tracking-wider">Token</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{p.vl_pib || '-'}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs">{p.vl_zastupnik_ime_prezime || '-'}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{p.pn_ime_prezime || '-'}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{p.pn_jmbg || '-'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDate(p.datum_izdavanja)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => print(p)} className="p-2 hover:bg-green-100 rounded" title="Stampaj" data-testid={`punomocje-print-${p.id}`}>
                        <Printer size={14} className="text-green-600" />
                      </button>
                      <button onClick={() => word(p)} className="p-2 hover:bg-indigo-100 rounded" title="Word dokument" data-testid={`punomocje-word-${p.id}`}>
                        <FileDoc size={14} className="text-indigo-600" />
                      </button>
                      <button onClick={() => sendEmail(p)} className="p-2 hover:bg-amber-100 rounded" title="Poslji na email" data-testid={`punomocje-email-${p.id}`}>
                        <EnvelopeSimple size={14} className="text-amber-600" />
                      </button>
                      <button onClick={() => handleEdit(p)} className="p-2 hover:bg-slate-200 rounded" title="Uredi" data-testid={`punomocje-edit-${p.id}`}>
                        <Pencil size={14} className="text-slate-600" />
                      </button>
                      <button onClick={() => handleDelete(p)} disabled={!isAdmin} className={`p-2 rounded ${isAdmin ? 'hover:bg-red-100' : 'opacity-30 cursor-not-allowed'}`} title={isAdmin ? 'Obrisi' : 'Samo administrator moze brisati'} data-testid={`punomocje-delete-${p.id}`}>
                        <Trash size={14} className="text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="punomocje-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Uredi punomoćje' : 'Novo specijalno punomoćje'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="pb-3 border-b border-slate-200">
              <Label className="text-xs font-semibold uppercase text-slate-500 mb-2 block">Vrsta punomocja *</Label>
              <div className="flex gap-2">
                <label className={`flex-1 flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer ${form.vrsta === 'OL' ? 'border-rose-500 bg-rose-50' : 'border-slate-200 hover:border-slate-300'}`} data-testid="punomocje-vrsta-ol">
                  <input type="radio" checked={form.vrsta === 'OL'} onChange={() => setForm({ ...form, vrsta: 'OL' })} />
                  <div>
                    <div className="text-sm font-semibold">Token (CoreSign OL)</div>
                    <div className="text-xs text-slate-500">Za izdavanje hardverskog tokena</div>
                  </div>
                </label>
                <label className={`flex-1 flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer ${form.vrsta === 'EFI' ? 'border-rose-500 bg-rose-50' : 'border-slate-200 hover:border-slate-300'}`} data-testid="punomocje-vrsta-efi">
                  <input type="radio" checked={form.vrsta === 'EFI'} onChange={() => setForm({ ...form, vrsta: 'EFI' })} />
                  <div>
                    <div className="text-sm font-semibold">Elektronski potpis (CoreSign EFI)</div>
                    <div className="text-xs text-slate-500">Za izdavanje digitalnog sertifikata za e-potpis</div>
                  </div>
                </label>
              </div>
            </div>

            <div className="pb-3 border-b border-slate-200">
              <Label className="text-xs font-semibold uppercase text-slate-500">Klijent (Vlastodavac) *</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <ClientAutocomplete clients={clients} value={form.client_id} onChange={(id) => ((v) => handleClientChange(v === 'none' ? '' : v))(id || 'none')} testid="punomocje-client-select" placeholder="Pretraga po nazivu ili PIB-u..." />
                </div>
                <Button type="button" variant="outline" onClick={refreshFromClient} disabled={!form.client_id} className="shrink-0 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="punomocje-refresh-client">
                  Povuci podatke
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-1">Izborom klijenta automatski se popunjavaju Vlastodavac podaci. Klik "Povuci podatke" refrešuje polja iz kartice klijenta.</p>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-bold uppercase text-slate-600">Vlastodavac (Grantor)</p>
              <div>
                <Label className="text-xs text-slate-500">Puni naziv</Label>
                <Input value={form.vl_naziv} onChange={(e) => setForm({ ...form, vl_naziv: e.target.value })} className="bg-white border-slate-300" placeholder='DRUSTVO SA OGRANICENOM ODGOVORNOSCU "..." ULCINJ' />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-slate-500">PIB</Label>
                  <Input value={form.vl_pib} onChange={(e) => setForm({ ...form, vl_pib: e.target.value })} className="bg-white border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Sjediste</Label>
                  <Input value={form.vl_sjediste} onChange={(e) => setForm({ ...form, vl_sjediste: e.target.value })} className="bg-white border-slate-300" placeholder="LIMAN BB, ULCINJ" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-slate-500">Zastupnik (Ime i prezime)</Label>
                  <Input value={form.vl_zastupnik_ime_prezime} onChange={(e) => setForm({ ...form, vl_zastupnik_ime_prezime: e.target.value })} className="bg-white border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">JMB zastupnika</Label>
                  <Input value={form.vl_zastupnik_jmb} onChange={(e) => setForm({ ...form, vl_zastupnik_jmb: e.target.value })} className="bg-white border-slate-300" placeholder="13 cifara" />
                </div>
              </div>
            </div>

            <div className="space-y-2 pt-3 border-t border-slate-200">
              <p className="text-xs font-bold uppercase text-slate-600">Punomocnik (Grantee) — bilo koje fizicko lice iz baze</p>
              <div>
                <Label className="text-xs text-slate-500">Ime i prezime *</Label>
                <FizickoLiceAutocomplete
                  value={form.pn_ime_prezime}
                  onChange={(v) => setForm(prev => ({ ...prev, pn_ime_prezime: v }))}
                  onSelect={(fl) => {
                    const ime = (fl.ime || '').toUpperCase();
                    const prezime = (fl.prezime || '').toUpperCase();
                    const fullNom = `${prezime} ${ime}`.trim();
                    const fullGen = toGenitivImePrezime(ime, prezime);
                    setForm(prev => ({
                      ...prev,
                      pn_ime_prezime: fullNom,
                      pn_ime_prezime_gen: fullGen,
                      pn_jmbg: fl.jmb || prev.pn_jmbg,
                      pn_broj_lk: fl.broj_dokumenta || prev.pn_broj_lk,
                    }));
                  }}
                  placeholder="Unesite 2+ slova: ime, prezime ili JMB..."
                  testid="punomocje-pn-autocomplete"
                />
                <p className="text-xs text-slate-400 mt-1">Pretraga po imenu, prezimenu ili JMB-u iz cijele baze fizickih lica. Izborom lica popunjavaju se JMBG, broj licne karte i forma u genitivu.</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Ime i prezime (u genitivu) — za stampu</Label>
                <Input
                  value={form.pn_ime_prezime_gen}
                  onChange={(e) => setForm({ ...form, pn_ime_prezime_gen: e.target.value })}
                  className="bg-white border-slate-300"
                  placeholder="npr. RADULA JOKOVIĆA"
                  data-testid="punomocje-pn-gen"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-slate-500">JMBG *</Label>
                  <Input value={form.pn_jmbg} onChange={(e) => setForm({ ...form, pn_jmbg: e.target.value })} className="bg-white border-slate-300" placeholder="13 cifara" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Broj lične karte</Label>
                  <Input value={form.pn_broj_lk} onChange={(e) => setForm({ ...form, pn_broj_lk: e.target.value })} className="bg-white border-slate-300" />
                </div>
              </div>
            </div>

            <div className="pt-3 border-t border-slate-200">
              <Label className="text-xs font-semibold uppercase text-slate-500">Datum izdavanja</Label>
              <DateInput value={form.datum_izdavanja} onChange={(e) => setForm({ ...form, datum_izdavanja: e.target.value })} className="border-slate-300" />
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Napomena (interno)</Label>
              <Input value={form.napomena} onChange={(e) => setForm({ ...form, napomena: e.target.value })} className="bg-white border-slate-300" placeholder="Opcionalno" />
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
              <Button variant="outline" onClick={() => setOpen(false)}>Otkazi</Button>
              <Button onClick={handleSave} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="punomocje-save">{editingId ? 'Sacuvaj' : 'Kreiraj'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
